/**
 * Dayvise API contracts and Zod validation schemas (prompt.md §63, §75).
 *
 * Provides shared, strongly-typed request and response schemas across the API,
 * web, desktop, and client SDKs matching true domain models and wire protocols.
 */

import { z } from "zod";
export * from "./profile.ts";

export const ReasoningEffortSchema = z.enum(["none", "minimal", "low", "medium", "high", "xhigh", "max", "ultra"]);

/* ---------------------------------- Auth ---------------------------------- */

export const LoginStartRequestSchema = z.object({
  provider: z.enum(["google", "github", "workos"]),
  redirectUri: z.string().url().optional(),
  codeChallenge: z.string().min(10).max(128).optional(),
  codeChallengeMethod: z.enum(["S256", "plain"]).optional(),
});
export type LoginStartRequest = z.infer<typeof LoginStartRequestSchema>;

export const TokenExchangeRequestSchema = z.object({
  code: z.string().min(1),
  code_verifier: z.string().min(10).max(128).optional(),
  redirect_uri: z.string().url().optional(),
});
export type TokenExchangeRequest = z.infer<typeof TokenExchangeRequestSchema>;

export const SessionUserSchema = z.object({
  userId: z.string(),
  displayName: z.string().nullable(),
  primaryEmail: z.string().nullable(),
});
export type SessionUser = z.infer<typeof SessionUserSchema>;

export const SessionWorkspaceSchema = z.object({
  workspaceId: z.string(),
  name: z.string(),
  kind: z.enum(["personal", "team"]),
  plan: z.string(),
  planStatus: z.enum(["effective", "cached"]).optional(),
  role: z.enum(["owner", "admin", "member", "viewer", "billing"]),
});
export type SessionWorkspace = z.infer<typeof SessionWorkspaceSchema>;

export const SessionResponseSchema = z.object({
  sessionId: z.string(),
  identityId: z.string().nullable().optional(),
  organizationId: z.string().nullable().optional(),
  user: SessionUserSchema.nullable(),
  plane: z.enum(["personal", "work"]).nullable(),
  activeWorkspaceId: z.string().nullable(),
  expiresAt: z.string().nullable(),
  workspaces: z.array(SessionWorkspaceSchema),
});
export type SessionResponse = z.infer<typeof SessionResponseSchema>;

/* --------------------------------- Agents --------------------------------- */

export const CreateAgentRequestSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  systemPrompt: z.string().max(10000).optional(),
  modelProvider: z.string().optional(),
  modelName: z.string().optional(),
  shared: z.boolean().optional().default(false),
});
export type CreateAgentRequest = z.infer<typeof CreateAgentRequestSchema>;

export const UpdateAgentRequestSchema = CreateAgentRequestSchema.partial();
export type UpdateAgentRequest = z.infer<typeof UpdateAgentRequestSchema>;

export const AgentResponseSchema = z.object({
  agentId: z.string(),
  workspaceId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  systemPrompt: z.string().nullable(),
  modelProvider: z.string().nullable(),
  modelName: z.string().nullable(),
  modelReasoningEffort: ReasoningEffortSchema.nullable().optional(),
  shared: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AgentResponse = z.infer<typeof AgentResponseSchema>;

/* ------------------------------ Conversations ----------------------------- */

export const CreateConversationRequestSchema = z.object({
  agentId: z.string().min(1),
  title: z.string().max(200).optional(),
  visibility: z.enum(["private", "workspace"]).optional().default("private"),
});
export type CreateConversationRequest = z.infer<typeof CreateConversationRequestSchema>;

export const SendMessageRequestSchema = z.object({
  text: z.string().min(1),
  /** False for editor-composed prose, even when it starts with a slash. */
  interpretSlash: z.boolean().optional(),
  branchId: z.string().optional(),
  model: z
    .object({
      provider: z.string(),
      model: z.string(),
      reasoningEffort: ReasoningEffortSchema.optional(),
    })
    .optional(),
  idempotencyKey: z.string().optional(),
  /**
   * Provider processing tier for this message. Validated against the resolved
   * provider; an unsupported tier is refused rather than silently ignored.
   */
  processingMode: z.enum(["default", "flex", "priority"]).optional(),
  /**
   * Sealed message secrets (secure text input). Each value is sealed server-side
   * into the message part; the model only ever sees `{{secret:<id>}}` handles.
   * At most 10 per message, 8192 chars each.
   *
   * `reuse` is the explicit opt-in to also store the value as a reusable
   * personal credential (visible, revocable, and reusable in later tasks).
   * Left out, the secret is task-scoped only and expires with its binding.
   */
  secrets: z
    .array(
      z.object({
        label: z.string().min(1).max(100),
        value: z.string().min(1).max(8192),
        reuse: z.boolean().optional(),
      }),
    )
    .max(10)
    .optional(),
  /**
   * Selected input: exact retained file revisions attached to this message. A
   * mutable file id alone is not enough — the accepted revision is bound here so
   * a later edit cannot change what the run receives.
   */
  attachments: z
    .array(
      z.object({
        fileId: z.string().min(1).max(128),
        version: z.number().int().min(1),
        name: z.string().min(1).max(255).optional(),
      }),
    )
    .max(20)
    .optional(),
});
export type SendMessageRequest = z.infer<typeof SendMessageRequestSchema>;

export const ConversationResponseSchema = z.object({
  conversationId: z.string(),
  workspaceId: z.string(),
  agentId: z.string(),
  ownerUserId: z.string(),
  title: z.string().nullable(),
  visibility: z.enum(["private", "workspace"]),
  defaultBranchId: z.string(),
  messageCount: z.number().optional().default(0),
  deletedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ConversationResponse = z.infer<typeof ConversationResponseSchema>;

/* ---------------------------------- Runs ---------------------------------- */

export const SteerRunRequestSchema = z.object({
  message: z.string().min(1).max(2000),
});
export type SteerRunRequest = z.infer<typeof SteerRunRequestSchema>;

export const PauseRunRequestSchema = z.object({
  reason: z.string().min(1).max(200).optional().default("user_requested"),
});
export type PauseRunRequest = z.infer<typeof PauseRunRequestSchema>;

export const ResumeRunRequestSchema = z.object({
  checkpointId: z.string().nullable().optional(),
});
export type ResumeRunRequest = z.infer<typeof ResumeRunRequestSchema>;

export const RunResponseSchema = z.object({
  runId: z.string(),
  workspaceId: z.string(),
  agentId: z.string(),
  conversationId: z.string(),
  branchId: z.string(),
  ownerUserId: z.string().optional(),
  trigger: z.enum(["user_message", "schedule", "webhook", "api", "agent_message", "resume", "retry"]),
  state: z.string(),
  pauseReason: z.string().nullable(),
  attempt: z.number(),
  maxAttempts: z.number(),
  checkpointId: z.string().nullable(),
  providerFallbackUsed: z.string().nullable().optional(),
  usage: z
    .object({
      inputTokens: z.number(),
      outputTokens: z.number(),
      toolCalls: z.number(),
      wallClockMs: z.number(),
      cpuMs: z.number(),
      containerMs: z.number(),
    })
    .optional(),
  startedAt: z.string().nullable().optional(),
  finishedAt: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type RunResponse = z.infer<typeof RunResponseSchema>;

/* -------------------------------- Schedules ------------------------------- */

export const CreateScheduleRequestSchema = z
  .object({
    agentId: z.string().min(1),
    title: z.string().min(1).max(200),
    prompt: z.string().min(1),
    cron: z.string().optional(),
    runAt: z.string().datetime().optional(),
    timezone: z.string().optional().default("UTC"),
    overlapPolicy: z.enum(["queue", "skip", "parallel"]).optional().default("queue"),
    missedRunPolicy: z.enum(["skip", "run_once", "run_all"]).optional().default("skip"),
    notifyOnCompletion: z.boolean().optional().default(true),
  })
  .refine((data) => (data.cron ? !data.runAt : !!data.runAt), {
    message: "Either cron or runAt must be specified, never both",
  });
export type CreateScheduleRequest = z.infer<typeof CreateScheduleRequestSchema>;

export const UpdateScheduleRequestSchema = z
  .object({
    title: z.string().min(1).max(200).optional(),
    prompt: z.string().min(1).optional(),
    cron: z.string().nullable().optional(),
    runAt: z.string().datetime().nullable().optional(),
    timezone: z.string().optional(),
    overlapPolicy: z.enum(["queue", "skip", "parallel"]).optional(),
    missedRunPolicy: z.enum(["skip", "run_once", "run_all"]).optional(),
    notifyOnCompletion: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.cron && data.runAt) return false;
      return true;
    },
    { message: "Either cron or runAt may be specified, never both" },
  );
export type UpdateScheduleRequest = z.infer<typeof UpdateScheduleRequestSchema>;

export const ScheduleResponseSchema = z.object({
  scheduleId: z.string(),
  workspaceId: z.string(),
  agentId: z.string(),
  title: z.string(),
  prompt: z.string(),
  cronExpression: z.string().nullable(),
  cron: z.string().nullable().optional(),
  runAt: z.string().nullable(),
  timezone: z.string(),
  enabled: z.boolean(),
  overlapPolicy: z.enum(["queue", "skip", "parallel"]),
  missedRunPolicy: z.enum(["skip", "run_once", "run_all"]),
  notifyOnCompletion: z.boolean(),
  status: z.enum(["active", "paused", "completed"]).optional(),
  nextRunAt: z.string().nullable(),
  lastRunAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ScheduleResponse = z.infer<typeof ScheduleResponseSchema>;

/* ----------------------------------- MCP ---------------------------------- */

export const CreateMcpServerRequestSchema = z.object({
  name: z.string().regex(/^[a-z0-9_-]+$/, "Server name must be lowercase alphanumeric with hyphens/underscores"),
  transport: z
    .union([
      z.enum(["http", "stdio"]),
      z.object({
        type: z.enum(["http", "stdio"]),
        url: z.string().url().optional(),
      }),
    ])
    .optional()
    .default("http"),
  url: z.string().url().optional(),
  auth: z
    .object({
      type: z.enum(["none", "bearer", "oauth"]).optional().default("none"),
      provider: z.string().optional(),
    })
    .optional(),
  tools: z
    .object({
      defaultEnabled: z.array(z.string()).optional(),
      defaultExcluded: z.array(z.string()).optional(),
    })
    .optional(),
  shared: z.boolean().optional().default(false),
  enabled: z.boolean().optional().default(true),
  credentialId: z.string().optional(),
});
export type CreateMcpServerRequest = z.infer<typeof CreateMcpServerRequestSchema>;

export const McpServerResponseSchema = z.object({
  mcpServerId: z.string(),
  workspaceId: z.string(),
  ownerUserId: z.string().nullable().optional(),
  name: z.string(),
  transport: z.string(),
  url: z.string().nullable(),
  authType: z.string(),
  credentialId: z.string().nullable().optional(),
  toolFilter: z.any().nullable().optional(),
  tools: z.array(z.any()).nullable().optional(),
  sourceUrl: z.string().nullable().optional(),
  shared: z.boolean(),
  enabled: z.boolean(),
  protocolVersion: z.string().nullable().optional(),
  health: z.enum(["unknown", "healthy", "unreachable", "unauthorized"]).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type McpServerResponse = z.infer<typeof McpServerResponseSchema>;

/* --------------------------------- Skills --------------------------------- */

export const CreateSkillRequestSchema = z
  .object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(1000).optional(),
    instructions: z.string().optional(),
    document: z.string().optional(),
    version: z.string().optional(),
    tags: z.array(z.string()).optional().default([]),
    requiredTools: z.array(z.string()).optional().default([]),
    requiredSecrets: z.array(z.string()).optional().default([]),
    visibility: z.enum(["private", "workspace", "organization"]).optional().default("workspace"),
  })
  .refine((data) => (data.document && data.document.length > 0) || (data.name && data.instructions), {
    message: "Either document (SKILL.md) or name and instructions must be provided",
  });
export type CreateSkillRequest = z.infer<typeof CreateSkillRequestSchema>;

export const SkillResponseSchema = z.object({
  skillId: z.string(),
  workspaceId: z.string(),
  ownerUserId: z.string().nullable().optional(),
  name: z.string(),
  version: z.string(),
  visibility: z.enum(["private", "workspace", "organization"]),
  frontmatter: z.record(z.any()).nullable().optional(),
  platforms: z.array(z.string()),
  tags: z.array(z.string()),
  category: z.string().nullable().optional(),
  source: z.enum(["builtin", "optional", "agent"]).or(z.string()),
  requiredTools: z.array(z.string()),
  requiredSecrets: z.array(z.string()),
  instructions: z.string(),
  compatibility: z.record(z.any()).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SkillResponse = z.infer<typeof SkillResponseSchema>;

/* ------------------------------ Notifications ----------------------------- */

export const NotificationResponseSchema = z.object({
  notificationId: z.string(),
  userId: z.string(),
  runId: z.string().nullable().optional(),
  kind: z.string(),
  title: z.string(),
  body: z.string().nullable().optional(),
  platforms: z.array(z.string()).optional().default([]),
  deliveredAt: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type NotificationResponse = z.infer<typeof NotificationResponseSchema>;

/* --------------------------------- Storage -------------------------------- */

export const StorageRouteSchema = z.object({
  structured: z.enum(["d1_shared", "postgres_byo"]),
  blob: z.enum(["r2_managed", "s3_byo"]),
  health: z.enum(["healthy", "degraded", "unavailable"]).optional(),
  lastCheckedAt: z.string().optional(),
});
export type StorageRoute = z.infer<typeof StorageRouteSchema>;

export const CreateCredentialRequestSchema = z.object({
  kind: z.enum(["ai_provider", "byo_database", "byo_blob", "secret"]),
  provider: z.string().min(1),
  label: z.string().min(1).max(100),
  secret: z.record(z.string()),
});
export type CreateCredentialRequest = z.infer<typeof CreateCredentialRequestSchema>;

/* -------------------------- Plans & Entitlements -------------------------- */

export const PlanSchema = z.enum(["plus", "pro", "max", "ultra", "teams", "enterprise"]);
export type Plan = z.infer<typeof PlanSchema>;

export const PlanTrialPolicySchema = z.object({
  trialDays: z.number().int().positive(),
  requiresCard: z.boolean(),
  cancelAnytime: z.boolean(),
  description: z.string(),
});
export type PlanTrialPolicyContract = z.infer<typeof PlanTrialPolicySchema>;

export const PlanPricingSchema = z.object({
  priceMonthlyUsd: z.number().nullable(),
  interval: z.enum(["month"]).nullable(),
  trial: PlanTrialPolicySchema.nullable(),
  description: z.string(),
});
export type PlanPricingContract = z.infer<typeof PlanPricingSchema>;

export const EntitlementsResponseSchema = z.object({
  plan: PlanSchema,
  pricing: PlanPricingSchema.optional(),
  byok: z.union([z.boolean(), z.object({ allowed: z.boolean() })]),
  byoDatabase: z.union([z.boolean(), z.object({ allowed: z.boolean() })]),
  byoBlob: z.union([z.boolean(), z.object({ allowed: z.boolean() })]),
  privateNetworking: z.union([z.boolean(), z.object({ allowed: z.boolean() })]).optional(),
  sharedMemory: z.union([z.boolean(), z.object({ allowed: z.boolean() })]).optional(),
  maxConcurrentRuns: z.number().nullable(),
  monthlyManagedAiTokens: z.number().nullable().optional(),
  monthlyContainerMs: z.number().nullable().optional(),
  monthlyCpuMs: z.number().nullable().optional(),
  storageBytes: z.number().nullable(),
  maxSchedules: z.number().nullable().optional(),
  maxAgents: z.number().nullable().optional(),
  maxTeamMembers: z.number().nullable().optional(),
  maxRetentionDays: z.number().nullable().optional(),
});
export type EntitlementsResponse = z.infer<typeof EntitlementsResponseSchema>;

export * from "./slash.ts";
export * from "./reasoning.ts";
export * from "./save.ts";
export * from "./personality.ts";
export * from "./forms.ts";
